import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { CadCompromisso } from '../interfaces/cad-compromisso';
import { PageResult } from '../interfaces/page-result';
import { JwtAuthService } from './auth/jwt-auth.service';

@Injectable({ providedIn: 'root' })
export class CadCompromissoService {

  readonly path = '/compromisso';

  constructor(private http: HttpClient, public jwtAuthService: JwtAuthService) {}

  create(): Observable<CadCompromisso> {
    return this.http.post<CadCompromisso>(`${this.jwtAuthService.apiUrl + this.path}`, null);
  }

  get(id: number): Observable<CadCompromisso> {
    return this.http.get<CadCompromisso>(`${this.jwtAuthService.apiUrl + this.path}/${id}`);
  }

  saveOrUpdate(compromisso: CadCompromisso): Observable<CadCompromisso> {
    return this.http.put<CadCompromisso>(`${this.jwtAuthService.apiUrl + this.path}`, compromisso);
  }

  delete(id: number): Observable<any> {
    return this.http.delete<any>(`${this.jwtAuthService.apiUrl + this.path}/${id}`);
  }

  getPage(page = 1, size = 20, order = 'NENHUM', direction = 'ASC'): Observable<PageResult<CadCompromisso>> {
    return this.http.get<PageResult<CadCompromisso>>(
      `${this.jwtAuthService.apiUrl + this.path}/page/get?page=${page}&size=${size}&order=${order}&direction=${direction}`
    );
  }

  getPageSearch(search: string, page = 1, size = 20, order = 'NENHUM', direction = 'ASC'): Observable<PageResult<CadCompromisso>> {
    return this.http.get<PageResult<CadCompromisso>>(
      `${this.jwtAuthService.apiUrl + this.path}/page/get?page=${page}&size=${size}&search=${encodeURIComponent(search)}&order=${order}&direction=${direction}`
    );
  }

  enviarLembrete(id: number): Observable<any> {
    return this.http.post<any>(`${this.jwtAuthService.apiUrl + this.path}/${id}/lembrete`, null);
  }

  /**
   * Monta os dados iniciais para o diálogo WhatsApp (WhatsappEditComponent):
   * telefone destino + mensagem padrão do lembrete (título, responsável, horário e prazo).
   */
  buildLembreteWhatsapp(c: CadCompromisso): { to: string; message: string } {
    return {
      to: (c.celular || '').replace(/\D/g, ''),
      message: this.buildLembreteMessage(c)
    };
  }

  private buildLembreteMessage(c: CadCompromisso): string {
    const pad = (n: number) => String(n).padStart(2, '0');
    const fmtDataHora = (d: Date) => {
      const dt = new Date(d);
      return `${pad(dt.getDate())}/${pad(dt.getMonth() + 1)}/${dt.getFullYear()} ${pad(dt.getHours())}:${pad(dt.getMinutes())}`;
    };
    const fmtHora = (d: Date) => {
      const dt = new Date(d);
      return `${pad(dt.getHours())}:${pad(dt.getMinutes())}`;
    };

    // Faixa de horário: "dd/MM/yyyy HH:mm – HH:mm" quando início e fim no mesmo dia
    let horario = '';
    if (c.dataInicio) {
      horario = fmtDataHora(c.dataInicio);
      if (c.dataFim) {
        const mesmoDia = new Date(c.dataInicio).toDateString() === new Date(c.dataFim).toDateString();
        horario += ' – ' + (mesmoDia ? fmtHora(c.dataFim) : fmtDataHora(c.dataFim));
      }
    } else if (c.dataFim) {
      horario = fmtDataHora(c.dataFim);
    }

    // Mantém só o nome do responsável (remove código no formato "000005654-NOME")
    const responsavel = (c.responsavel || '').replace(/^\s*\d{3,}\s*[-–]\s*/, '').trim();

    let msg = '📋 *Lembrete de Compromisso*\n\n';
    msg += `*${c.titulo}*`;
    if (c.descricao && c.descricao.trim()) msg += `\n${c.descricao.trim()}`;
    if (responsavel)                       msg += `\n\n👤 ${responsavel}`;
    if (horario)                           msg += `\n⏰ Horário: ${horario}`;
    if (c.dataFim)                         msg += `\n📅 Prazo: ${fmtDataHora(c.dataFim)}`;
    return msg;
  }

  checkConflito(dataInicio: Date, dataFim: Date, excludeUuid?: string): Observable<{ temConflito: boolean; conflitos: any[] }> {
    // Envia data/hora LOCAL (sem conversão para UTC) para casar com o LocalDateTime do backend
    let url = `${this.jwtAuthService.apiUrl + this.path}/check-conflito` +
      `?dataInicio=${this.toLocalIso(dataInicio)}&dataFim=${this.toLocalIso(dataFim)}`;
    if (excludeUuid) url += `&excludeUuid=${excludeUuid}`;
    return this.http.get<{ temConflito: boolean; conflitos: any[] }>(url);
  }

  /** Formata a data como ISO local (YYYY-MM-DDTHH:mm:ss) sem aplicar offset de timezone */
  private toLocalIso(date: Date): string {
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
      `T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
  }

  getPendentesHoje(): Observable<CadCompromisso[]> {
    return this.http.get<CadCompromisso[]>(`${this.jwtAuthService.apiUrl + this.path}/pendentes/hoje`);
  }

  getPagePeriodo(dataInicial: Date, dataFinal: Date, page = 1, size = 20, order = 'NENHUM', direction = 'ASC'): Observable<PageResult<CadCompromisso>> {
    return this.http.get<PageResult<CadCompromisso>>(
      `${this.jwtAuthService.apiUrl + this.path}/page/get?page=${page}&size=${size}` +
      `&dataInicial=${dataInicial.toISOString()}&dataFinal=${dataFinal.toISOString()}&order=${order}&direction=${direction}`
    );
  }
}
